// --- Dependencies -------------------------------------------------------------------------------
import { useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useTranslation from "@/hooks/useTranslation";
import Icon from "@/UI/atoms/Icon";
import FeatureToggle from "@/UI/inputs/FeatureToggle";
import NumberInput from "@/UI/inputs/NumberInput";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
export type TyUnitFieldValues = Record<string, number | boolean | null>;

export type TyUnitFieldsDetails = {
    title: string;
    /** Every field this group can hold, in the order the constants list them. */
    keys: readonly string[];
    /** What each one is called, already translated. */
    labels: Record<string, string>;
    /** A count takes a number; a feature takes yes, no, or nobody said. */
    kind: "counts" | "flags";
    values: TyUnitFieldValues;
    /**
     * What the record held when this opened. The strip at the top is the difference between it and
     * the draft — which is the only way somebody who has scrolled thirty rooms can see the four they
     * actually touched, and take any of them back one at a time.
     */
    baseline?: TyUnitFieldValues;
    /**
     * **Hand back only what was answered.** The single unit form wants every key, so a count cleared
     * here is written as `null` rather than left at whatever the record held. A bulk write wants the
     * opposite and needs it badly: returning all thirty keys would blank every room and feature on
     * every unit in the selection, which is the one mistake in this feature a screen could not undo.
     */
    partial?: boolean;
    onConfirm: (values: TyUnitFieldValues) => void;
};

type Props = { onClose: () => void; details?: unknown };

/**
 * Every field of one group, in one list.
 *
 * The form behind this shows only what has been answered, because a unit holds fourteen counts and
 * fifteen features and almost none holds more than four of either. Here is the opposite job: the
 * whole set at once, so a run of them can be read down and filled in without opening anything.
 *
 * `null` is "nobody said", and it is what the form reads to decide what to show. Clearing a count's
 * box takes the fact back; a feature has its own third mark for it.
 */
export default function UnitFieldsPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const { title, keys, labels, kind, values, baseline, partial, onConfirm } = details as TyUnitFieldsDetails;

    const [draft, setDraft] = useState<TyUnitFieldValues>(() => ({ ...values }));

    const set = (key: string, value: number | boolean | null) => setDraft((current) => ({ ...current, [key]: value }));

    /** What started where. Without one, every answer in the group reads as a change — which it is. */
    const was = baseline ?? values;
    const moved = keys.filter((key) => (draft[key] ?? null) !== (was[key] ?? null));

    const revert = (key: string) => set(key, was[key] ?? null);
    const revertAll = () => setDraft(() => ({ ...was }));

    /** One answer in words, for the strip. The marks carry it in the list; here there is no room. */
    const say = (key: string) => {
        const value = draft[key] ?? null;
        if (value === null) return t.properties.notSet;
        if (kind === "counts") return String(value);
        return value ? t.properties.yes : t.properties.no;
    };

    const handleConfirm = () => {
        // Partial: only what moved. Otherwise every key the group knows, so a field cleared here is
        // written back as `null` rather than left at whatever the record held before.
        const picked = partial ? moved : keys;
        onConfirm(Object.fromEntries(picked.map((key) => [key, draft[key] ?? null])));
        onClose();
    };

    return (
        <section className="popup-card unit-fields">
            <h2 className="unit-fields__title">{title}</h2>

            <p className="unit-fields__hint">{kind === "counts" ? t.properties.countsHint : t.properties.flagsHint}</p>

            {/* What has moved, before the thirty rows that could have moved it — so the four answers
                somebody actually gave are readable without scrolling back up through the group. */}
            {moved.length > 0 && (
                <div className="unit-fields__moved">
                    <div className="unit-fields__moved-head">
                        <span className="unit-fields__moved-title">{t.bulk.willChange}</span>
                        <button type="button" className="unit-fields__revert-all" onClick={revertAll}>
                            {t.bulk.revertAll}
                        </button>
                    </div>

                    <div className="unit-fields__chips">
                        {moved.map((key) => (
                            <span className="unit-fields__chip" key={key}>
                                <span className="unit-fields__chip-label">{labels[key]}</span>
                                <strong className="unit-fields__chip-value">{say(key)}</strong>
                                <button
                                    type="button"
                                    className="unit-fields__chip-drop"
                                    onClick={() => revert(key)}
                                    aria-label={t.bulk.keep}
                                    title={t.bulk.keep}
                                >
                                    <Icon name="close" size={10} />
                                </button>
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {/* A column of counts, or a column of features — the whole group, nothing hidden. */}
            <div className={`unit-fields__list unit-fields__list--${kind}`}>
                {keys.map((key) =>
                    kind === "counts" ? (
                        <NumberInput key={key} value={(draft[key] as number) ?? null} onChange={(value) => set(key, value)} label={labels[key]} />
                    ) : (
                        <FeatureToggle key={key} value={(draft[key] as boolean) ?? null} onChange={(value) => set(key, value)} label={labels[key]} />
                    )
                )}
            </div>

            <div className="unit-fields__actions">
                <button type="button" className="unit-fields__cancel" onClick={onClose}>
                    {t.common.cancel}
                </button>
                <button type="button" className="unit-fields__confirm" onClick={handleConfirm}>
                    {t.common.done}
                </button>
            </div>
        </section>
    );
}
